import React, { useEffect, useMemo, useState } from 'react';
import EntityTableHeader, {
  EntityTableHeaderProps,
  IconsList
} from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';

import { AppTable, SlColumn, SlRow, useStacklineTheme } from '@stackline/ui';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { UseTableDataProps } from 'src/serverProxy/useTableData';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { calculatePercentChange, getAppName } from 'src/utils/app';
import { isAtlasCostMetric } from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { Field } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import _get from 'lodash/get';
import { ColDef } from 'ag-grid-community';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import { GRID_ITEMS_PER_PAGE, ProductGridProps } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { Box } from '@mui/material';

export const FETCH_CHUNK_SIZE = 100;

export interface HeadColumnDefinition {
  headerName: string;
  valueFormatter: (value: unknown, row: unknown) => JSX.Element;
}

export interface MetricListContainerField extends Field {
  /**
   * If false, will still fetch the metric but will not show
   * it as a column in the table view
   */
  displayInTable?: boolean;
  wrapperStyle?: React.CSSProperties;
}

export interface AtlasV2MetricListContainerProps {
  indexName: string;
  /**
   * How the response will be sorted
   */
  sortFieldName: string;
  mainFieldName?: string;
  fields: MetricListContainerField[];
  entityTableHeaderProps: Omit<
    EntityTableHeaderProps,
    'enableSwitchingLayouts' | 'defaultView' | 'verticalInset' | 'handleChangeLayout' | 'eventBus'
  >;
  showGrid?: boolean;
  forceGrid?: boolean;
  showHeader?: boolean;
  suffixOverride?: string;
  useTableDataProps?: Partial<Omit<UseTableDataProps, 'fields' | 'indexName'>>;
  headColumnDefinition: HeadColumnDefinition | HeadColumnDefinition[];
  loading?: boolean;
  rowCount?: number;
  renderGrid?: (
    args: Pick<ProductGridProps, 'loading' | 'metricType' | 'page' | 'data' | 'fieldName' | 'noDataPlaceholder'>
  ) => JSX.Element;
  showSortOptions?: boolean;
  groupBy?: string;
  additionalColumnDefs?: HeadColumnDefinition[];
  iconList?: IconsList[];
  skeletonRows?: number;
  columnDefinitions?: ColDef[];
  buildRequest?: (requestBuilder: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder;
  postProcessData?: (data: any[]) => any[];
  onChangePage?: (args: {
    page: number;
    /**
     * Page sent to advanced search, not the one displayed.
     * For example, we could fetch 100 items at at time but only display 20
     * in the grid, so if the page is 6, the adjusted page would be 2.
     *
     * It will be calculated as Math.ceil(page / PAGES_PER_CHUNK)
     */
    chunkedPage: number;
  }) => void;
  appTableProps?: Partial<React.ComponentProps<typeof AppTable>>;
  customDataSource?: {
    data: any[];
    isLoading: boolean;
    getValuesByFieldName: (name: string) => any[];
    getValueDifferencesByFieldName: (name: string) => any[];
    totalFilteredCount?: number;
  };
  shouldHidePagination?: boolean;
  /**
   * Controlled sort option from parent
   */
  sortOption?: GridSortOptions;
  /**
   * Callback when sort option changes
   */
  onSortOptionChange?: (sortOption: GridSortOptions) => void;
}

export enum GridSortOptions {
  TopSelling = 'topSelling',
  Increasing = 'increasing',
  Decreasing = 'decreasing'
}

/**
 * Smart container for rendering a grid. It can switch between the grid
 * and table, and displays the pagination
 *

 */
const AtlasV2MetricListContainer = ({
  indexName,
  sortFieldName,
  mainFieldName,
  fields,
  entityTableHeaderProps,
  showGrid = true,
  forceGrid = false,
  showHeader = true,
  suffixOverride,
  useTableDataProps,
  headColumnDefinition,
  loading,
  rowCount,
  renderGrid,
  showSortOptions,
  groupBy = 'stacklineSku',
  additionalColumnDefs = [],
  iconList = [IconsList.export, IconsList.tile, IconsList.table],
  skeletonRows,
  columnDefinitions,
  buildRequest,
  postProcessData,
  onChangePage,
  appTableProps = {},
  shouldHidePagination = false,
  customDataSource,
  sortOption: controlledSortOption,
  onSortOptionChange
}: AtlasV2MetricListContainerProps) => {
  const { isProductEntity } = useProductEntity();

  const PAGES_PER_CHUNK = useMemo(
    () => Math.ceil((_get(useTableDataProps, 'itemsPerPage') || FETCH_CHUNK_SIZE) / GRID_ITEMS_PER_PAGE),
    [useTableDataProps]
  );

  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const [sortOption, setSortOption] = useState<GridSortOptions>(controlledSortOption || GridSortOptions.TopSelling);
  const [page, setPage] = useState(1);
  const [listView, setListView] = useState<'tile' | 'table'>(
    (showGrid && !isProductEntity) || forceGrid ? 'tile' : 'table'
  );
  const theme = useStacklineTheme();
  const [dataByPageNumber, setDataByPageNumber] = useState({} as { [key: number]: any[] });

  // Use controlled sortOption if provided
  const actualSortOption = controlledSortOption !== undefined ? controlledSortOption : sortOption;

  useEffect(() => {
    if (!showGrid) {
      setListView('table');
    }
  }, [showGrid]);

  const iconsList = useMemo(() => {
    if (showGrid) {
      return iconList;
    }
    return iconList.filter((icon) => icon !== IconsList.tile);
  }, [iconList, showGrid]);

  const handleChangeLayout = () => {
    if (showGrid) {
      setListView((oldView) => (oldView === 'table' ? 'tile' : 'table'));
    }
  };

  const advancedSearchDataSource = useAtlasV2TableData<{ metadata: any }>({
    fields: [...fields],
    groupByFieldName: groupBy,
    indexName,
    requestId: 'product-grid',
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: Math.ceil(page / PAGES_PER_CHUNK),
    sortFieldName,
    suffixOverride,
    buildRequest,
    shouldPerformFetch: !customDataSource,
    ...useTableDataProps
  });

  const {
    isLoading: tableRowsLoading,
    getValuesByFieldName,
    getValueDifferencesByFieldName,
    data: tableRows
  } = customDataSource || advancedSearchDataSource;

  const tableData = useMemo(() => {
    const fieldValuesMap = fields.reduce((acc, { name }) => {
      return {
        ...acc,
        [name]: {
          values: getValuesByFieldName(name),
          differences: getValueDifferencesByFieldName(name)
        }
      };
    }, {} as { values: number[]; differences: number[] });

    return tableRows?.map((row, index) => ({
      ...(isProductEntity ? { weekId: row.weekId } : {}),
      ...row.metadata,
      ...fields.reduce((acc, { name }) => {
        return {
          ...acc,
          [name]: {
            value: fieldValuesMap[name].values[index],
            difference: fieldValuesMap[name].differences[index]
          }
        };
      }, {} as { [key: string]: number }),
      responseRow: row
    }));
  }, [fields, getValueDifferencesByFieldName, getValuesByFieldName, isProductEntity, tableRows]);

  // Hide the pagination controls if we have less than the max amount of grid items per page on the first page of results.
  // This does not handle the edge case where we could have the exact amount of grid items per page.
  const hidePagination =
    shouldHidePagination ||
    ((rowCount < GRID_ITEMS_PER_PAGE || (tableData || []).length < GRID_ITEMS_PER_PAGE) && page === 1 && !loading);

  const totalPages = Math.max(
    Math.ceil(
      (rowCount !== undefined ? rowCount : customDataSource?.totalFilteredCount || tableRows.length) /
        GRID_ITEMS_PER_PAGE
    ),
    1
  ); // Always show page 1 of 1 if no results exist

  /**
   * Updates the full data set so that we can sort
   * by increasing or decreasing
   */
  useEffect(() => {
    setDataByPageNumber((oldData) => ({
      ...oldData,
      [Math.ceil(page / PAGES_PER_CHUNK)]: tableData
    }));
  }, [tableData, page, PAGES_PER_CHUNK]);

  /**
   * The full table data, sorted by the sort option.
   */
  const fullTableData = useMemo(() => {
    if (customDataSource) {
      return tableData;
    }

    const updatedTableData = Object.keys(dataByPageNumber)
      .sort((a, b) => Number(a) - Number(b))
      .reduce((acc, pageNumber) => {
        return [...acc, ...dataByPageNumber[Number(pageNumber)]];
      }, []);

    if (Object.keys(updatedTableData?.[0] ?? {}).includes(sortFieldName)) {
      const data = updatedTableData
        .sort((a, b) => {
          switch (actualSortOption) {
            case GridSortOptions.TopSelling:
              return b[sortFieldName]?.value - a[sortFieldName]?.value;
            case GridSortOptions.Increasing:
              return b[sortFieldName]?.difference - a[sortFieldName]?.difference;
            case GridSortOptions.Decreasing:
              return a[sortFieldName]?.difference - b[sortFieldName]?.difference;
            default:
              return 0;
          }
        })
        .filter((d) => {
          const { difference } = d[mainFieldName || sortFieldName];

          if (actualSortOption === GridSortOptions.Increasing) {
            return difference >= 0;
          } else if (actualSortOption === GridSortOptions.Decreasing) {
            return difference <= 0;
          }

          return true;
        });

      if (postProcessData) {
        return postProcessData(data);
      }
      return data;
    } else {
      return updatedTableData;
    }
  }, [customDataSource, tableData, dataByPageNumber, mainFieldName, postProcessData, sortFieldName, actualSortOption]);

  const field = useMemo(
    () => INDEX_FIELDS.getField(getAppName(), indexName, mainFieldName || sortFieldName),
    [indexName, mainFieldName, sortFieldName]
  );

  /**
   * Returns the page number we are using to fetch. For example, if we show 20 items
   * per page and fetch 100 items, then page 1-5 will be 1, page 6-10 will be 2, etc.
   */
  const computeChunkedPage = (displayedPage: number) => {
    return Math.ceil(displayedPage / PAGES_PER_CHUNK);
  };

  const handleNextPage = () => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const startIndex = (adjustedPage - 1) * GRID_ITEMS_PER_PAGE;
    const endIndex = startIndex + GRID_ITEMS_PER_PAGE;

    if (tableRows.slice(startIndex, endIndex).length < GRID_ITEMS_PER_PAGE) {
      return;
    }

    const newPage = page + 1;
    setPage(newPage);

    if (onChangePage) {
      onChangePage({
        page: newPage,
        chunkedPage: computeChunkedPage(newPage)
      });
    }
  };

  // go to the first page when sortOption changes or the time period options change
  useEffect(() => {
    setPage(1);
  }, [actualSortOption, startWeek, endWeek, comparisonStartWeek, comparisonEndWeek]);

  const handlePrevPage = () => {
    const newPage = Math.max(1, page - 1);
    setPage(newPage);

    if (onChangePage) {
      onChangePage({
        page: newPage,
        chunkedPage: computeChunkedPage(newPage)
      });
    }
  };

  return (
    <SlColumn widths="full" spacing={showSortOptions ? 'none' : 'lg'}>
      {showHeader ? (
        <EntityTableHeader
          enableSwitchingLayouts
          defaultView={listView}
          handleChangeLayout={handleChangeLayout}
          iconsList={iconsList}
          paddingTop="0px"
          paddingBottom="0px"
          {...entityTableHeaderProps}
          handleGroupByChange={(event) => {
            setPage(1);
            setDataByPageNumber({});
            if (entityTableHeaderProps.handleGroupByChange) {
              entityTableHeaderProps.handleGroupByChange(event);
            }
          }}
        />
      ) : null}
      {showSortOptions && (
        <GenericNavigationTabs
          tab={actualSortOption}
          tabs={[
            {
              label: 'Top-Selling',
              value: GridSortOptions.TopSelling,
              onClick: () => {
                const newSortOption = GridSortOptions.TopSelling;
                setSortOption(newSortOption);
                onSortOptionChange?.(newSortOption);
              }
            },
            {
              label: 'Increasing',
              value: GridSortOptions.Increasing,
              onClick: () => {
                const newSortOption = GridSortOptions.Increasing;
                setSortOption(newSortOption);
                onSortOptionChange?.(newSortOption);
              }
            },
            {
              label: 'Decreasing',
              value: GridSortOptions.Decreasing,
              onClick: () => {
                const newSortOption = GridSortOptions.Decreasing;
                setSortOption(newSortOption);
                onSortOptionChange?.(newSortOption);
              }
            }
          ]}
          tabsStyle={{
            paddingTop: theme.spacing.md,
            paddingBottom: theme.spacing.lg
          }}
        />
      )}
      <SlColumn widths="full">
        {listView === 'tile' ? (
          renderGrid ? (
            renderGrid({
              data: fullTableData,
              loading: tableRowsLoading || loading,
              metricType: field.metricType,
              page,
              fieldName: mainFieldName || sortFieldName,
              noDataPlaceholder: (
                <Box sx={{ display: 'flex', justifyContent: 'center', margin: '80px 0' }}>
                  <NoDataPlaceHolder
                    title="Data currently unavailable"
                    description="There is currently no data matching your selected criteria. Please try another query to view insights."
                    flexProps={{
                      justifyContent: 'center',
                      padding: 0
                    }}
                  />
                </Box>
              )
            })
          ) : null
        ) : (
          <EntityTable
            skeletonRows={skeletonRows}
            isLoading={loading || tableRowsLoading}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            showPagination={false}
            shouldModifyColumnDefs={false}
            columnDefs={
              columnDefinitions || [
                ...(headColumnDefinition
                  ? Array.isArray(headColumnDefinition)
                    ? headColumnDefinition
                    : [headColumnDefinition]
                  : []),
                ...fields
                  .filter(({ displayInTable }) => displayInTable !== false)
                  .map(({ name: columnFieldName, displayName, comparisonFormatter, wrapperStyle }) => {
                    const columnField = INDEX_FIELDS.getField(getAppName(), indexName, columnFieldName);

                    return {
                      headerName: displayName || columnField.displayName,
                      valueFormatter: (_, row) => {
                        const metricValue = row?.[columnFieldName]?.value;
                        const metricDiff = row?.[columnFieldName]?.difference;

                        return (
                          <MetricCell
                            value={metricValue}
                            wrapperStyle={wrapperStyle}
                            metricType={columnField.metricType}
                            percentChange={
                              comparisonFormatter
                                ? undefined
                                : calculatePercentChange(metricValue, metricValue - metricDiff)
                            }
                            comparisonNode={
                              comparisonFormatter ? comparisonFormatter(metricValue, metricDiff) : undefined
                            }
                            isAdSpendMetric={isAtlasCostMetric(columnFieldName)}
                          />
                        );
                      }
                    };
                  }),
                ...additionalColumnDefs
              ]
            }
            rowData={fullTableData}
            appTableProps={{
              noDataPlaceholder: (
                <NoDataPlaceHolder
                  title="Data currently unavailable"
                  description="There is currently no data matching your selected criteria. Please try another query to view insights."
                  flexProps={{
                    paddingTop: '12.5px',
                    paddingBottom: '12.5px'
                  }}
                />
              ),
              ...appTableProps
            }}
          />
        )}
        <div style={{ marginTop: listView === 'tile' ? '20px' : undefined }}>
          {hidePagination ? null : (
            <SlRow horizontalPosition="end">
              <Pagination
                currentPage={page}
                shouldUseInfinitePagination
                onClickNext={() => handleNextPage()}
                onClickPrevious={() => handlePrevPage()}
              />
            </SlRow>
          )}
        </div>
      </SlColumn>
    </SlColumn>
  );
};

export default AtlasV2MetricListContainer;
